import { DialogActions, DialogContent, Grid, useMediaQuery, useTheme } from '@mui/material';
import CancellationDueDeathContent from 'cancellations/components/cancellation-due-death/content';
import CancellationDueDeathStepButtons from 'cancellations/components/cancellation-due-death/step-buttons';
import { CancellationDueDeathFormCrumbs } from 'cancellations/constants/cancellation-due-death-crumbs';
import { cancellationValidationSchema } from 'cancellations/constants/cancellation-validation-schema';
import {
  transformAnprRequestDeath,
  transformAnprResponse,
} from 'cancellations/utils/transform-anpr-response';
import { CancellationDueDeath } from 'cancellations/view-models/cancellation-due-death';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { createSearchParams, generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PmiDialog } from 'shell/components/shared/dialog';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import {
  OkResponse,
  ResponseAnprData,
  ResponseAnprSaveData,
  SubjectData,
} from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PmiButton } from 'shell/components/shared/button';
import PmiTitle from 'shell/components/shared/title';

interface CancellationDueDeathPageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  initialValues?: CancellationDueDeath;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const CancellationDueDeathPage = ({
  isWithoutBreadcrumbs,
  disabledFromAct,
  initialValues,
  onConfirm,
  onCancel,
}: CancellationDueDeathPageProps): React.ReactElement => {
  const [queryParams] = useSearchParams();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  //const navigate = useNavigate();
  const openCancelDialog = () => setIsCancelDialogOpen(true);
  const closeCancelDialog = () => setIsCancelDialogOpen(false);
  const navigate = useNavigate();

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const coniuge = JSON.parse(queryParams.get('coniuge') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const [anprResponse, getAnprResponse] = useHttpFetch<HttpResponse<ResponseAnprData>, OkResponse>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => response.data.payload?.risposta3002OK,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCancellation, saveCancellation] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/cancellazione-morte`, {
    onBootstrap: false,
    transformer: transformResponseSaveDataAnpr,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
  });

  const castedAnprData = responseCancellation.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const [family, getFamily] = useHttpFetch<HttpResponse<ResponseAnprData>, SubjectData[]>(
    `${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: (response) =>
        response?.data?.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto || [],
    }
  );

  useEffect(() => {
    (
      getAnprResponse({
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, OkResponse>
    ).then((personResponse) => {
      const typedAnpr = personResponse as OkResponse;
      getFamily({
        data: {
          criteriRicerca: {
            identificativoANPR: (typedAnpr?.listaSoggetti?.datiSoggetto[0] || {}).famigliaConvivenza
              ?.idFamigliaConvivenzaANPR,
            codiceFiscale: fiscalCode || undefined,
            tipoScheda: '1',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch === 'true' ? '1' : '0',
            // Fixed value for request family
            schedaAnagraficaRichiesta: '1',
            // Fixed value for request family
            datiAnagraficiRichiesti: ['02', '10', '21'],
          },
        },
      });
    });
  }, [fiscalCode, idSchedaSoggettoANPR]);

  const handleSubmit = (form: CancellationDueDeath) => {
    const data = transformAnprRequestDeath(
      form,
      anprResponse.data as OkResponse,
      family.data as SubjectData[],
      fiscalCode,
      coniuge
    );

    (
      saveCancellation({
        data,
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    ).then((response) => {
      const castedResponse = response as SaveDataAnpr;

      setVisibility({ error: undefined, warning: undefined });

      if (castedResponse?.errors?.length) {
        window.scrollTo(0, 0);
        return;
      }

      toast.success(`Cancellazione di ${form.generalityForm?.fiscalCode} eseguita con successo`);

      if (onConfirm) {
        onConfirm();
        return;
      }

      // Da fare redirect sul coniuge, capire quale va preso
      if (form.deathDataForm?.deathPartnerResident) {
        let marriedSubject;

        if (form.subjectData?.matrimonio) {
          marriedSubject = form.subjectData?.matrimonio?.coniuge;
        }

        if (form.subjectData?.unioneCivile) {
          marriedSubject = form.subjectData?.unioneCivile?.unitoCivilmente;
        }

        if (!marriedSubject || !marriedSubject?.idSchedaSoggettoANPR) {
          toast.warning(
            `Non si può procedere al caricamento dei dati del coniuge, perché il codice fiscale risulta non univoco ${
              marriedSubject?.codiceFiscale?.codFiscale || ''
            }`
          );
          navigate('/cancellazioni/cancellazione-per-morte');
          return;
        }

        navigate({
          pathname: generatePath('/cancellazioni/cancellazione-per-morte/cancellazione'),
          search: `?${createSearchParams({
            ricercaNazionale: nationalSearch,
            codiceFiscale: marriedSubject?.codiceFiscale?.codFiscale || '',
            idSchedaSoggettoANPR: marriedSubject?.idSchedaSoggettoANPR || '',
            coniuge: 'true',
          })}`,
        });
        return;
      }

      navigate('/cancellazioni/cancellazione-per-morte');
    });
  };

  const initialForm: CancellationDueDeath | undefined = useMemo(() => {
    if (
      !anprResponse.data ||
      !family.data ||
      anprResponse.isLoading ||
      anprResponse.pristine ||
      family.isLoading ||
      family.pristine
    ) {
      return undefined;
    }
    return transformAnprResponse(anprResponse.data as OkResponse, family.data as SubjectData[]);
  }, [
    anprResponse.data,
    family.data,
    anprResponse.isLoading,
    anprResponse.pristine,
    family.isLoading,
    family.pristine,
  ]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  if (!initialForm) {
    return <></>;
  }

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : <Breadcrumbs items={CancellationDueDeathFormCrumbs} />}
      <PmiTitle title="Cancellazione per morte" paddingBottom={1} />
      <div className="pmi-mt-2">
        <Formik
          initialValues={{ ...(initialForm || {}), ...(initialValues || {}) }}
          onSubmit={handleSubmit}
          validationSchema={cancellationValidationSchema}
        >
          <Form>
            <Grid container spacing={1}>
              <Grid item xs={12} md={2}>
                <CancellationDueDeathStepButtons onCancel={openCancelDialog} />
              </Grid>
              <Grid item xs={12} md={10}>
                <div className="pmi-px-md-4">
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorMessage.title || ''}
                    messages={errorMessage.messages || []}
                    visibility={errorMessage.visibility || false}
                    onChangeVisibility={onVisibility}
                  />
                  <SearchMessages
                    type={MessageType.Warning}
                    title={
                      castedAnprData?.warnings?.length === 1
                        ? `C'è 1 warning`
                        : `Ci sono ${castedAnprData?.warnings?.length} warning`
                    }
                    messages={castedAnprData?.warnings || []}
                    visibility={
                      visibility.warning !== undefined
                        ? visibility.warning
                        : (castedAnprData?.warnings || [])?.length > 0
                    }
                    onChangeVisibility={onVisibility}
                  />
                  <CancellationDueDeathContent disabledFromAct={disabledFromAct} />
                </div>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </div>
      <PmiDialog
        open={isCancelDialogOpen}
        onClose={closeCancelDialog}
        aria-labelledby="close-modal"
        aria-describedby="close-modal"
        fullWidth
        fullScreen={fullScreen}
      >
        <PmiDialogTitle id="close-modal-title" backgroundColor="primary" color="white">
          Stampe
        </PmiDialogTitle>
        <DialogContent style={{ padding: '30px 24px' }}>
          Annulla operazione comporta la perdita di tutti i dati digitati, si è sicuri di voler
          continuare?
        </DialogContent>
        <DialogActions>
          <PmiButton
            variant="contained"
            color="cdmBrand"
            type="button"
            onClick={() => {
              if (onCancel) {
                onCancel();
                return;
              }
              navigate('/cancellazioni/cancellazione-per-morte');
            }}
          >
            Conferma Annullo
          </PmiButton>
          <PmiButton variant="outlined" color="primary" type="button" onClick={closeCancelDialog}>
            Chiudi
          </PmiButton>
        </DialogActions>
      </PmiDialog>
    </Fragment>
  );
};

CancellationDueDeathPage.displayName = 'Cancellation due death page component';

export default CancellationDueDeathPage;
